/**
 * bs-clarity.js — friction tracking for the ballstories book configurator.
 *
 * Answers one question: where do customers hesitate and drop out inside the
 * configurator? Sends custom events and tags to Microsoft Clarity (loaded as an
 * app embed, so `window.clarity` may legitimately be absent).
 *
 * Works purely off the DOM. The configurator marks its steps and fields with
 * `data-bs-step` / `data-bs-field` and emits a `bs:track` CustomEvent for the
 * few things that are not observable from markup (preview duration, upload
 * rejections, empty city search). That keeps this file the single place where
 * tracking logic lives, and it works no matter whether the configurator was
 * loaded from the app extension or from the theme fallback asset.
 *
 * Privacy: no field value ever leaves this file. Only schema field paths, step
 * ids, fixed reason codes and durations are reported.
 *
 * Internally events are canonical (name + params). Only `toClarity()` knows
 * about Clarity. Every canonical event is additionally re-emitted on `document`
 * as `bs:event` (and every tag as `bs:tag`), which is the seam other sinks hang
 * off — `bs-ga4.js` is one. Sinks never observe the DOM themselves, so the
 * friction logic stays in exactly one place.
 *
 * Debug output: append `?bs_track=debug` to the URL.
 */
(function () {
  "use strict";

  var HESITATE_MS = 10000; // focused, still empty → hesitation
  var SKIP_MIN_MS = 3000; // min. focus time before "left empty" counts
  var SLOW_STEP_MS = 90000; // step taking this long is a friction signal
  var SLOW_PREVIEW_MS = 15000; // AI preview wait felt long
  var CHIP_SWITCH_MIN = 3; // this many re-decisions on one chip group
  var TAG_LIST_MAX = 15; // keep tag values well below Clarity's 255 chars
  var MOUNT_POLL_MS = 300;
  var MOUNT_POLL_TRIES = 50; // ~15s, covers the deferred configurator boot

  // Das Debug-Flag überlebt die Navigation: der Konfigurator sitzt auf den
  // Produktseiten, und wer mit `?bs_track=debug` auf der Startseite startet,
  // verliert den Parameter beim ersten Klick — genau dann, wenn es interessant
  // wird. `?bs_track=off` schaltet wieder ab.
  function debugEnabled() {
    var on = /[?&]bs_track=debug/.test(window.location.search);
    try {
      if (on) window.sessionStorage.setItem("bs_track_debug", "1");
      else if (/[?&]bs_track=off/.test(window.location.search)) {
        window.sessionStorage.removeItem("bs_track_debug");
        return false;
      }
      return on || window.sessionStorage.getItem("bs_track_debug") === "1";
    } catch (e) {
      return on;
    }
  }

  var DEBUG = debugEnabled();

  var state = {
    active: false, // configurator visible (modal open, or inline)
    opened: false,
    completed: false,
    step: "",
    stepAt: 0,
    maxStep: 0,
    bookType: "",
    seenSteps: {},
    fields: {},
    friction: [],
    errorFields: [],
    previewAt: 0,
    once: {},
    birthdayStarted: false,
    birthdayDone: false,
  };

  // ---------------------------------------------------------------------------
  // Clarity bridge — the only place that knows about Clarity
  // ---------------------------------------------------------------------------

  function clarityCall() {
    if (typeof window.clarity !== "function") return false;
    try {
      window.clarity.apply(null, arguments);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Turns `child.birthday` into `child_birthday` — Clarity event names should
  // stay readable in the dashboard.
  function slug(v) {
    return String(v || "")
      .replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  // Clarity events carry no parameters, so the dimension has to live in the
  // name. Everything aggregatable goes into tags instead (see `tag()`).
  var NAME_MAP = {
    open: "bs_open",
    step_enter: "bs_step",
    step_back: "bs_back",
    step_slow: "bs_slow",
    exit: "bs_exit",
    exit_page: "bs_exit_page",
    field_hesitate: "bs_slow",
    field_skip: "bs_skip",
    field_rework: "bs_rework",
    chip_switch: "bs_switch",
    info_open: "bs_info",
    field_error: "bs_err",
    field_error_repeat: "bs_err2",
    preview_start: "bs_preview_start",
    preview_done: "bs_preview_done",
    preview_slow: "bs_preview_slow",
    preview_error: "bs_preview_error",
    preview_exit: "bs_preview_exit",
    upload_start: "bs_upload_start",
    upload_done: "bs_upload_done",
    upload_error: "bs_upload_error",
    city_no_result: "bs_city_no_result",
    city_search_error: "bs_city_search_error",
    city_typed_not_selected: "bs_city_typed_not_selected",
    reject_age_under5: "bs_reject_age_under5",
    cart_error: "bs_cart_error",
    complete: "bs_complete",
  };

  // Only events whose meaning depends on *which* field is involved carry the
  // field name. Upload, city and preview events always refer to one known
  // field, so appending it would just make the dashboard harder to read.
  var FIELD_SCOPED = {
    field_hesitate: 1,
    field_skip: 1,
    field_rework: 1,
    chip_switch: 1,
    info_open: 1,
    field_error: 1,
    field_error_repeat: 1,
  };

  var STEP_SCOPED = { step_enter: 1, step_back: 1, step_slow: 1, exit: 1, exit_page: 1 };

  function toClarity(name, params) {
    var p = params || {};
    var base = NAME_MAP[name] || "bs_" + slug(name);
    if (name === "complete") return p.mode === "add_only" ? "bs_complete_addonly" : "bs_complete";
    if (name === "upload_blocked") return "bs_upload_" + slug(p.reason);
    if (FIELD_SCOPED[name] && p.field) return base + "_" + slug(p.field);
    if (STEP_SCOPED[name]) return base + "_" + slug(p.step);
    return base;
  }

  // Re-emits a canonical event for other sinks (bs-ga4.js). Never throws into
  // the tracking path: a broken sink must not take Clarity down with it.
  function emit(type, detail) {
    try {
      if (typeof window.CustomEvent !== "function") return;
      document.dispatchEvent(new CustomEvent(type, { detail: detail }));
    } catch (e) {}
  }

  function track(name, params) {
    var eventName = toClarity(name, params);
    if (DEBUG) console.log("[bs-track]", eventName, name, params || {});
    clarityCall("event", eventName);
    emit("bs:event", { name: name, params: params || {} });
  }

  // Fires an event name verbatim. Only used for the four legacy `birthdate_*`
  // events that already have history in Clarity.
  function trackRaw(eventName) {
    if (DEBUG) console.log("[bs-track] (legacy)", eventName);
    clarityCall("event", eventName);
  }

  function tag(key, value) {
    if (DEBUG) console.log("[bs-track] tag", key, value);
    clarityCall("set", key, value);
    emit("bs:tag", { key: key, value: value });
  }

  // Fires at most once per page load.
  function trackOnce(key, name, params) {
    if (state.once[key]) return;
    state.once[key] = true;
    track(name, params);
  }

  function addToList(list, value, tagKey) {
    if (!value || list.indexOf(value) !== -1) return;
    list.push(value);
    if (list.length <= TAG_LIST_MAX) tag(tagKey, list.slice());
  }

  // ---------------------------------------------------------------------------
  // Field state
  // ---------------------------------------------------------------------------

  function fieldState(path) {
    if (!state.fields[path]) {
      state.fields[path] = {
        focusAt: 0,
        settled: null, // value as of the first blur that had content
        blurs: 0,
        chipClicks: 0,
        errors: 0,
        hesitated: false,
        skipped: false,
        reworked: false,
        infoSeen: false,
      };
    }
    return state.fields[path];
  }

  function markFriction(path) {
    addToList(state.friction, path, "bs_friction_field");
  }

  function fieldWrap(node) {
    if (!node || typeof node.closest !== "function") return null;
    return node.closest("[data-bs-field]");
  }

  function valueOf(node) {
    if (!node) return "";
    if (node.type === "checkbox" || node.type === "radio") return node.checked ? "1" : "";
    return String(node.value == null ? "" : node.value).trim();
  }

  function isInput(node) {
    if (!node || !node.tagName) return false;
    var t = node.tagName.toLowerCase();
    return t === "input" || t === "textarea" || t === "select";
  }

  // ---------------------------------------------------------------------------
  // Step transitions
  // ---------------------------------------------------------------------------

  function stepIndexOf(stepKey) {
    var n = parseInt(String(stepKey).split("_")[0], 10);
    return isNaN(n) ? 0 : n;
  }

  function leaveStep() {
    if (!state.step || !state.stepAt) return;
    if (Date.now() - state.stepAt > SLOW_STEP_MS) {
      trackOnce("slowstep:" + state.step, "step_slow", { step: state.step });
    }
  }

  function enterStep(stepKey) {
    if (!stepKey || stepKey === state.step) return;
    var goingBack = state.step && stepIndexOf(stepKey) < stepIndexOf(state.step);
    var from = state.step;
    leaveStep();
    state.step = stepKey;
    state.stepAt = Date.now();
    tag("bs_last_step", stepKey);
    var idx = stepIndexOf(stepKey);
    if (idx > state.maxStep) {
      state.maxStep = idx;
      tag("bs_max_step", stepKey);
    }
    // A backwards jump means the customer wants to correct something.
    if (goingBack && from) track("step_back", { step: from });
    if (!state.seenSteps[stepKey]) {
      state.seenSteps[stepKey] = true;
      track("step_enter", { step: stepKey });
    }
  }

  // Nothing is reported while the configurator is closed: it renders itself on
  // page load and re-renders on every interaction, so this runs a lot.
  function readStepFrom(root) {
    if (!state.active) return;
    var section = root.querySelector("[data-bs-step]");
    if (!section) return;
    var bookType = section.getAttribute("data-bs-book-type") || "";
    if (bookType && bookType !== state.bookType) {
      state.bookType = bookType;
      tag("bs_book_type", bookType);
    }
    enterStep(section.getAttribute("data-bs-step"));
  }

  // ---------------------------------------------------------------------------
  // Activation and exit
  // ---------------------------------------------------------------------------

  function activate(root) {
    if (state.active) return;
    state.active = true;
    if (!state.opened) {
      state.opened = true;
      tag("bs_outcome", "abandoned"); // corrected on completion
      track("open");
    }
    if (root) readStepFrom(root);
  }

  function exit(kind) {
    if (!state.active || state.completed) {
      state.active = false;
      return;
    }
    state.active = false;
    leaveStep();
    // Dropped out while waiting for the AI preview.
    if (state.previewAt) track("preview_exit", { step: state.step });
    if (state.birthdayStarted && !state.birthdayDone) trackRaw("birthdate_closed_empty");
    track(kind === "page" ? "exit_page" : "exit", { step: state.step || "unknown" });
  }

  // ---------------------------------------------------------------------------
  // Listeners
  // ---------------------------------------------------------------------------

  // Capture phase throughout: the configurator re-renders on nearly every
  // interaction, so listeners must not rely on surviving elements, and `toggle`
  // does not bubble at all.

  document.addEventListener(
    "focusin",
    function (e) {
      var wrap = fieldWrap(e.target);
      if (!wrap || !isInput(e.target)) return;
      var path = wrap.getAttribute("data-bs-field");
      var fs = fieldState(path);
      var empty = !valueOf(e.target);
      fs.focusAt = Date.now();

      if (path === "child.birthday" && empty && !state.birthdayStarted) {
        state.birthdayStarted = true;
        trackRaw("birthdate_started");
      }

      if (!empty || fs.hesitated) return;
      window.setTimeout(function () {
        // Still the focused field and still empty → real hesitation.
        var activeWrap = fieldWrap(document.activeElement);
        if (!activeWrap || activeWrap.getAttribute("data-bs-field") !== path) return;
        if (valueOf(document.activeElement) || fs.hesitated) return;
        fs.hesitated = true;
        markFriction(path);
        track("field_hesitate", { field: path });
        if (path === "child.birthday") trackRaw("birthdate_hesitated_10s");
      }, HESITATE_MS);
    },
    true
  );

  document.addEventListener(
    "focusout",
    function (e) {
      var wrap = fieldWrap(e.target);
      if (!wrap || !isInput(e.target)) return;
      var path = wrap.getAttribute("data-bs-field");
      var fs = fieldState(path);
      var value = valueOf(e.target);
      var held = fs.focusAt ? Date.now() - fs.focusAt : 0;
      fs.blurs++;
      fs.focusAt = 0;

      if (!value) {
        // Required field left empty after actually spending time in it. The
        // minimum duration filters out focus loss caused by a re-render.
        if (!fs.skipped && wrap.getAttribute("data-bs-required") === "1" && held >= SKIP_MIN_MS) {
          fs.skipped = true;
          markFriction(path);
          track("field_skip", { field: path });
        }
        return;
      }
      if (fs.settled === null) {
        fs.settled = value;
        return;
      }
      // Value changed again after it had already been settled once.
      if (value !== fs.settled && !fs.reworked) {
        fs.reworked = true;
        markFriction(path);
        track("field_rework", { field: path });
      }
      fs.settled = value;
    },
    true
  );

  document.addEventListener(
    "change",
    function (e) {
      var wrap = fieldWrap(e.target);
      if (!wrap || !isInput(e.target)) return;
      if (wrap.getAttribute("data-bs-field") !== "child.birthday") return;
      if (valueOf(e.target) && !state.birthdayDone) {
        state.birthdayDone = true;
        trackRaw("birthdate_completed");
      }
    },
    true
  );

  document.addEventListener(
    "click",
    function (e) {
      var target = e.target;
      if (!target || typeof target.closest !== "function") return;

      // Repeated re-decisions inside one chip group signal uncertainty.
      var chip = target.closest(".bs-c__chip");
      if (chip) {
        var chipWrap = fieldWrap(chip);
        if (chipWrap) {
          var chipPath = chipWrap.getAttribute("data-bs-field");
          var cfs = fieldState(chipPath);
          cfs.chipClicks++;
          if (cfs.chipClicks - 1 >= CHIP_SWITCH_MIN) {
            markFriction(chipPath);
            trackOnce("switch:" + chipPath, "chip_switch", { field: chipPath });
          }
        }
      }

      if (target.closest("[data-bs-close]") || target.closest(".bs-modal__close")) exit("close");
    },
    true
  );

  // Opening the info tooltip means the label alone did not explain the field.
  document.addEventListener(
    "toggle",
    function (e) {
      var details = e.target;
      if (!details || !details.classList || !details.classList.contains("bs-c__info") || !details.open) return;
      var wrap = fieldWrap(details);
      if (!wrap) return;
      var path = wrap.getAttribute("data-bs-field");
      var fs = fieldState(path);
      if (fs.infoSeen) return;
      fs.infoSeen = true;
      track("info_open", { field: path });
    },
    true
  );

  // Signals emitted by the configurator itself.
  document.addEventListener("bs:track", function (e) {
    var detail = (e && e.detail) || {};
    var name = detail.name;
    var params = detail.params || {};
    if (!name) return;
    if (params.step) state.step = params.step || state.step;

    switch (name) {
      case "field_error":
        var path = params.field;
        var fs = fieldState(path);
        fs.errors++;
        markFriction(path);
        addToList(state.errorFields, path, "bs_error_field");
        track("field_error", { field: path });
        // Repeating the same error means the message did not help.
        if (fs.errors >= 2) trackOnce("err2:" + path, "field_error_repeat", { field: path });
        if (params.reason === "age_under_5") trackOnce("age5", "reject_age_under5");
        if (params.reason === "not_selected_from_list") {
          trackOnce("citysel", "city_typed_not_selected");
        }
        return;

      case "preview_start":
        state.previewAt = Date.now();
        track("preview_start");
        return;

      case "preview_done":
        state.previewAt = 0;
        track("preview_done");
        if (Number(params.ms) > SLOW_PREVIEW_MS) track("preview_slow");
        return;

      case "preview_error":
        state.previewAt = 0;
        track("preview_error");
        return;

      case "complete":
        state.completed = true;
        leaveStep();
        tag("bs_outcome", "completed");
        track("complete", params);
        return;

      default:
        track(name, params);
    }
  });

  window.addEventListener("pagehide", function () {
    exit("page");
  });
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") exit("page");
  });

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  // The configurator scripts are deferred and the app extension may load late,
  // so poll briefly instead of assuming the mount already exists.
  function waitForMount(cb) {
    var tries = 0;
    var timer = window.setInterval(function () {
      var mount = document.querySelector("[data-bs-mount]");
      if (mount) {
        window.clearInterval(timer);
        cb(mount);
        return;
      }
      if (++tries >= MOUNT_POLL_TRIES) window.clearInterval(timer);
    }, MOUNT_POLL_MS);
  }

  function boot() {
    waitForMount(function (mount) {
      // Step changes and error markers are only visible after a re-render.
      new MutationObserver(function () {
        readStepFrom(mount);
      }).observe(mount, { childList: true, subtree: true });

      var dialog = document.querySelector("[data-bs-modal]");
      if (dialog) {
        // Modal mode: the configurator renders while still closed, so the
        // `open` attribute — not the presence of a step — marks the real start.
        if (dialog.open) activate(mount);
        new MutationObserver(function () {
          if (dialog.open) activate(mount);
          else exit("close");
        }).observe(dialog, { attributes: true, attributeFilter: ["open"] });
      } else {
        // Inline mode: visible as soon as it is rendered.
        activate(mount);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
